import { Box, chakra, useSlotRecipe, type BoxProps } from '@chakra-ui/react';
import { segmentedControlSlotRecipe } from '../../theme/recipes/segmented-control';
import { motion, useReducedMotion } from 'framer-motion';
import { useCallback, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';

export type RiftboundSegmentedControlItem<T extends string> = {
  value: T;
  label: string;
};

export type RiftboundSegmentedControlSize = 'small' | 'medium' | 'large';

type RiftboundSegmentedControlProps<T extends string> = {
  value: T;
  items: readonly RiftboundSegmentedControlItem<T>[];
  onValueChange: (value: T) => void;
  'aria-label'?: string;
  /** Stretch the track. Omit to size the control to its labels. */
  w?: BoxProps['w'];
  /** Medium is the page-level control. Small is for dense toolbars. */
  size?: RiftboundSegmentedControlSize;
  flexShrink?: BoxProps['flexShrink'];
};

const SEPARATOR = { small: '12px', medium: '14px', large: '16px' } as const;

const THUMB_SPRING = { type: 'spring' as const, stiffness: 520, damping: 42 };

const FOCUS_CSS = {
  WebkitTapHighlightColor: 'transparent',
  '&:focus': { outline: 'none' },
  // Mobile Safari often leaves a focus ring after a tap. The thumb already
  // shows selection, so drop the ring on touch pointers.
  '@media (hover: none), (pointer: coarse)': {
    '&:focus-visible': { outline: 'none' },
  },
} as const;

type ThumbFrame = { x: number; y: number; width: number; height: number };

/**
 * iOS segmented control: a quiet well, equal segments, and a raised thumb
 * that slides. Selection is label color, not a filled accent.
 * The thumb is measured from the selected segment so it stays aligned when
 * labels or the track width change.
 */
export function RiftboundSegmentedControl<T extends string>({
  value,
  items,
  onValueChange,
  'aria-label': ariaLabel,
  w,
  size = 'medium',
  flexShrink,
}: RiftboundSegmentedControlProps<T>) {
  const reduceMotion = useReducedMotion();
  const count = items.length;
  const selectedIndex = Math.max(0, items.findIndex((item) => item.value === value));
  const recipe = useSlotRecipe({ recipe: segmentedControlSlotRecipe });
  const chrome = recipe({ size });
  const selectedItem = recipe({ size, selected: true }).item;
  const restingItem = recipe({ size, selected: false }).item;
  const separator = SEPARATOR[size];
  const trackRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [thumb, setThumb] = useState<ThumbFrame | null>(null);

  const measure = useCallback(() => {
    const track = trackRef.current;
    const button = buttonRefs.current[selectedIndex];
    if (!track || !button) return;
    const trackRect = track.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    setThumb({
      x: buttonRect.left - trackRect.left,
      y: buttonRect.top - trackRect.top,
      width: buttonRect.width,
      height: buttonRect.height,
    });
  }, [selectedIndex]);

  useLayoutEffect(() => {
    measure();
  }, [measure, items, size, w, count]);

  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(track);
    return () => observer.disconnect();
  }, [measure]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const delta = event.key === 'ArrowRight' || event.key === 'ArrowDown'
      ? 1
      : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
        ? -1
        : 0;
    if (!delta || count === 0) return;
    event.preventDefault();
    const next = (selectedIndex + delta + count) % count;
    onValueChange(items[next].value);
  };

  return (
    <Box
      ref={trackRef}
      role="radiogroup"
      aria-label={ariaLabel}
      position="relative"
      display={w ? 'grid' : 'inline-grid'}
      gridAutoFlow="column"
      gridAutoColumns="1fr"
      w={w}
      flexShrink={flexShrink}
      css={chrome.track}
      onKeyDown={onKeyDown}
    >
      <motion.div
        aria-hidden
        initial={false}
        animate={{
          x: thumb?.x ?? 0,
          y: thumb?.y ?? 0,
          width: thumb?.width ?? 0,
          height: thumb?.height ?? 0,
        }}
        transition={reduceMotion ? { duration: 0 } : THUMB_SPRING}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          borderRadius: 9999,
          background: 'var(--chakra-colors-surface)',
          boxShadow: '0 1px 2px rgba(0,0,0,0.35), inset 0 0.5px 0 rgba(255,255,255,0.14)',
          pointerEvents: 'none',
          opacity: thumb ? 1 : 0,
        }}
      />
      {items.map((item, index) => {
        const selected = item.value === value;
        const separatorHidden = index === selectedIndex || index === selectedIndex + 1;
        return (
          <Box key={item.value} position="relative" minW={0} display="flex">
            {index > 0 ? (
              <Box
                aria-hidden
                pointerEvents="none"
                position="absolute"
                left={0}
                top="50%"
                w="1px"
                h={separator}
                bg="whiteAlpha.400"
                transform="translateY(-50%)"
                opacity={separatorHidden ? 0 : 1}
                transition="opacity 160ms ease"
                zIndex={1}
              />
            ) : null}
            <chakra.button
              type="button"
              role="radio"
              aria-checked={selected}
              tabIndex={selected ? 0 : -1}
              ref={(node: HTMLButtonElement | null) => {
                buttonRefs.current[index] = node;
              }}
              flex="1"
              position="relative"
              zIndex={2}
              css={{ ...(selected ? selectedItem : restingItem), ...FOCUS_CSS }}
              whiteSpace="nowrap"
              cursor="pointer"
              transition="color 160ms ease"
              onClick={() => onValueChange(item.value)}
            >
              {item.label}
            </chakra.button>
          </Box>
        );
      })}
    </Box>
  );
}
